import { useState } from 'react'
import { userLocale } from '../../lib/locale'
import { Section } from './Section'

/** What useWatch hands over — the account's one watch, and the code to replace it. */
export interface WatchState {
  pairedAt: string | null
  /** The paired watch's name, when its part number is one we know. */
  model: string | null
  code: string | null
  busy: boolean
  generateCode: () => void
  dismissCode: () => void
  unpair: () => void
}

// One watch per person (022): pairing again replaces it. A code is minted only
// on a tap, never because the panel opened from the watch's QR (?pair): any
// link could otherwise make a signed-in phone mint a live code on its own.
export function WatchSection({ watch, householdName, autoAddWatch }: {
  watch: WatchState
  householdName: string
  autoAddWatch?: boolean
}) {
  const [confirm, setConfirm] = useState(false)
  return (
    <Section title="Garmin watch">
      <div className="ml-settings__card">
        {watch.code ? (
          <div className="ml-watch-code">
            <p className="ml-settings__sub">
              Enter this code on your watch. It expires in 5 minutes.
              {watch.pairedAt && ' It replaces the watch paired now.'}
            </p>
            <p className="ml-watch-code__code ml-num">{watch.code}</p>
            <button type="button" className="ml-text-btn ml-text-btn--quiet" onClick={watch.dismissCode}>Done</button>
          </div>
        ) : watch.pairedAt ? (
          <>
            <p className="ml-settings__line">{watch.model ?? 'Watch'}</p>
            <p className="ml-settings__sub">
              Paired with {householdName} since {new Date(watch.pairedAt).toLocaleDateString(userLocale(), { day: 'numeric', month: 'short' })}. One watch per person.
            </p>
            <div className="ml-settings__actions">
              <button type="button" className="ml-text-btn" disabled={watch.busy} onClick={watch.generateCode}>
                {watch.busy ? 'One moment…' : 'Pair a different watch'}
              </button>
              {confirm ? (
                <>
                  <button type="button" className="ml-text-btn ml-text-btn--destructive" aria-label="Confirm disconnect"
                    disabled={watch.busy} onClick={() => { watch.unpair(); setConfirm(false) }}>Disconnect</button>
                  <button type="button" className="ml-text-btn ml-text-btn--quiet" onClick={() => setConfirm(false)}>Cancel</button>
                </>
              ) : (
                <button type="button" className="ml-text-btn ml-text-btn--quiet" onClick={() => setConfirm(true)}>Disconnect</button>
              )}
            </div>
          </>
        ) : (
          <>
            {autoAddWatch && <p className="ml-settings__sub">Your watch is ready to pair. Tap Add watch for a code.</p>}
            <p className="ml-settings__sub">Keep the list on your wrist while you shop.</p>
            <button type="button" className="ml-text-btn" disabled={watch.busy} onClick={watch.generateCode}>
              {watch.busy ? 'One moment…' : 'Add watch'}
            </button>
          </>
        )}
      </div>
    </Section>
  )
}
